"use client";
import { formatDateLong } from "@/utils/formatDate";
import { Eye, Send } from "lucide-react";
import Image from "next/image";
import React from "react";
import { FaFacebookF, FaHeart, FaLinkedinIn } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { IBlogImage, IBlogRecord } from "../../interface/IBlogInterface";

const BlogDetails = ({
  blogDetailsData,
}: {
  blogDetailsData?: IBlogRecord | null;
}) => {
  const [active, setActive] = React.useState(0);
  const details = blogDetailsData ?? null;
  const imageGallery = details?.blog_images || [];
  return (
    <div>
      {" "}
      <div className="padding-x my-10 flex flex-col md:flex-row justify-center gap-10">
        <div className="order-2 md:order-1 md:w-[60%]">
          <div className="overflow-hidden rounded-none md:rounded-3xl h-[415px] relative">
            {(() => {
              const activeImage =
                imageGallery?.[active]?.image ||
                details?.image ||
                "/placeholder.png";

              return (
                <Image
                  src={activeImage}
                  alt={`slide-${active}`}
                  fill
                  sizes="(max-width: 768px) 100vw, 60vw"
                  className="object-cover rounded-3xl"
                  priority
                />
              );
            })()}
          </div>

          {imageGallery?.length > 1 && (
            <div className="absolute left-[30%] bottom-4 md:bottom-10 lg:bottom-[35%] transform -translate-x-1/2 flex gap-2 bg-black/30 rounded-full px-3 py-1">
              {imageGallery?.map((_: IBlogImage, i: number) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    i === active ? "bg-white" : "bg-white/50"
                  }`}
                />
              ))}
            </div>
          )}

          <div className="mt-10">
            <p
              className="typography-mid-body-light text-grey-700 mb-5"
              dangerouslySetInnerHTML={{
                __html: details?.description || "",
              }}
            ></p>
          </div>
          <div className="flex flex-row justify-between bg-primary-500 p-5 mb-10 rounded-xl items-center">
            <p className="typography-extra-large-body text-white">
              Like what you see? Share with a friend.
            </p>
            <div className="flex flex-row">
              <button className="p-2 bg-white rounded-lg mr-4 flex items-center justify-center">
                <FaFacebookF className="text-green-500" size={24} />
              </button>

              <button className="p-2 bg-white rounded-lg mr-4 flex items-center justify-center">
                <FaXTwitter className="text-green-500" size={24} />
              </button>

              <button className="p-2 bg-white rounded-lg flex items-center justify-center">
                <FaLinkedinIn className="text-green-500" size={24} />
              </button>
            </div>
          </div>
          <hr className="border-grey-900" />
        </div>
        <div className="order-1 md:order-2 md:w-[40%]">
          <div className="sticky top-30">
            <hr className="border-grey-900 mb-5" />
            <p className="inline-flex gap-2 px-3 py-2 rounded-3xl bg-green-50 text-grey-600 text-sm mr-5">
              <FaHeart className="inline-block text-red-500" size={18} />{" "}
              {details?.like_count || 0}
            </p>
            <p className="inline-flex gap-2 px-3 py-2 rounded-3xl bg-green-50 text-grey-600 text-sm mr-5">
              <Eye className="inline-block text-grey-500" size={18} />{" "}
              {details?.view_count || 0}
            </p>
            <p className="inline-flex gap-2 px-3 py-2 rounded-3xl bg-green-50 text-grey-600 text-sm">
              <Send className="inline-block text-grey-500" size={18} /> 2
            </p>
            <hr className="border-grey-900 my-5" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Publication Date</p>
                <p className="font-medium">
                  {details?.created_at
                    ? formatDateLong(details.created_at)
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Category</p>
                <p className="font-medium">{details?.category || "-"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Reading Time</p>
                <p className="font-medium">10 Min</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Author Name</p>
                <p className="font-medium">{details?.author || "Admin"}</p>
              </div>
            </div>
            <div>
              <p className="text-gray-500 my-4">Table Of Contents</p>
              <div className="bg-yellow-50 p-5 rounded-xl">
                {/* Build TOC from bold/strong/headings inside description HTML */}
                {(() => {
                  const html = details?.description || "";
                  const regex =
                    /<(?:b|strong|h[1-6])[^>]*>(.*?)<\/(?:b|strong|h[1-6])>/gi;
                  const items: string[] = [];
                  let m: RegExpExecArray | null;
                  while ((m = regex.exec(html)) !== null) {
                    const text = m[1].replace(/<[^>]+>/g, "").trim();
                    if (text && !items.includes(text)) items.push(text);
                  }

                  if (items.length === 0) {
                    return (
                      <p className="text-sm text-gray-600">
                        No table of contents available
                      </p>
                    );
                  }

                  return (
                    <ul className="list-disc pl-5 space-y-2">
                      {items.map((t, i) => (
                        <li
                          key={i}
                          className="font-medium text-sm text-gray-700"
                        >
                          {t}
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetails;
